import { useAzureIp } from "../../../context/Dashboard/azure/contextIp.js";
import { useDesign } from "../../../context/contextDesign.js";

const ScanCard = () => {
  const {
    ipScanLoading, 
    ipDashboardView,
    handleSubmit, 
    setIp, 
    ip, 
    error
  } = useAzureIp();
  const { toggleUpgradeCard } = useDesign();

  const ipExamples = [
    { label: "Public IP", value: "20.123.45.67" },
    { label: "Static IP", value: "40.210.15.44" },
    { label: "Azure DNS", value: "my-vm.eastus.cloudapp.azure.com" }
  ];

  return (
    <div className="scan-card d-flex justify-content-center align-items-center">
      <div className="card shadow-sm border-1 rounded-4">
        <div className="d-flex flex-row align-items-center h-100">

          {/* 🔹 Left: Form Section */}
          <div className="scan-form-section p-3 w-50">
            <form
              onSubmit={handleSubmit}
              className="d-flex flex-column justify-content-center align-items-center"
            >
              <div className="fs-1 fw-bold text-center heading mb-3">
                Scan VMs IP
              </div>

              <div
                className="fs-6 text-center fw-semibold mb-5 subheading text-muted"
                style={{ width: 280 }}>
                Enter the vm ip below to scan for potential vulnerabilities.
              </div>

              <div
                className="mb-2 w-100 input-group rounded-2"
                style={{ height: 43 }}
              >
                <input
                  type="text"
                  placeholder="Enter VM IP"
                  className="p-2 text-center form-control"
                  onChange={(e) => setIp(e.target.value)}
                  disabled={ipScanLoading || ipDashboardView}
                />
              </div>

              <div style={{ width: 260 }}>
                {error && (
                  <div
                    className="error-hover text-danger text-center fw-semibold mb-2"
                    style={{ cursor: 'pointer', fontSize: '14px' }}
                    onClick={error.card ? toggleUpgradeCard : null}>
                    {error.message}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-success w-100 py-2 fw-semibold fs-6"
                disabled={ipScanLoading || ipDashboardView || !ip}
                style={{ height: 46 }}
              >
                Submit
              </button>
            </form>
          </div>

          {/* 🔹 Right: Documentation Section */}
          <div className="scan-video-section h-100 w-50 p-4 bg-light d-flex flex-column border-start overflow-auto">
            {/* Documentation Header */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-microsoft text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">AZURE IP DOCUMENTATION</span>
              </div>
              <p className="text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                An Azure Virtual Machine (VM) is a virtual server running in the Azure cloud. To scan correctly, ensure your IP is a public IP address. Vulnerability scanning requires a reachable Public IP address associated with an active Azure Virtual Machine (VM).
              </p>
            </div>

            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-shield-lock-fill text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">AZURE VM DEPLOYMENT GUIDE</span>
              </div>
              <p className="text-muted" style={{ fontSize: '12.5px', lineHeight: '1.5' }}>
                Follow these steps to ensure your Azure instance is correctly configured for external security assessment.
              </p>
            </div>

            {/* Full Process Steps */}
            <div className="d-flex flex-column gap-4 mb-4">

              {/* Step 1: VM Instance State */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 1</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>VM Deployment</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Log in to <strong>Azure Portal</strong>, navigate to <strong>Virtual Machines</strong>, and click <strong>Create</strong>.</>,
                    <>Ensure the VM status is <strong>Running</strong> before initiating a vulnerability scan.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 2: Network Security Groups (NSG) */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 2</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Security Configuration</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Navigate to <strong>Networking</strong> and check your <strong>Inbound port rules</strong> (NSG).</>,
                    <>Allow traffic on necessary ports (e.g., 80, 443) to permit the scanner to reach the VM.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 3: IP Retrieval */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 3</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Identify Public IP</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Select your VM and find the <strong>Public IP address</strong> in the <strong>Overview</strong> essentials tab.</>,
                    <>Ensure a <strong>DNS name label</strong> is configured if you prefer scanning via hostname.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Supported Examples Footer */}
            <div className="mt-auto pt-3 border-top">
              <div className="mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-info-circle-fill text-success"></i>
                <span className="fw-bold small text-secondary">VALID INPUT FORMATS</span>
              </div>

              <div className="d-flex flex-column gap-2">
                {ipExamples.map((ex, i) => (
                  <div key={i} className="p-2 rounded bg-white border border-light-subtle shadow-sm">
                    <div className="text-primary fw-bold mb-1" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{ex.label}</div>
                    <code className="text-dark-emphasis d-block text-truncate p-1 bg-light rounded" style={{ fontSize: '11px' }}>
                      {ex.value}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanCard;